import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useConfirm } from "@/context/ConfirmContext";
import { Mail, RotateCcw, Send, Eye, ShieldCheck } from "lucide-react";

function TemplateEditor({ t, onSaved }) {
  const confirm = useConfirm();
  const [subject, setSubject] = useState(t.subject);
  const [body, setBody] = useState(t.body);
  const [cta, setCta] = useState(t.cta_label || "");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setSubject(t.subject); setBody(t.body); setCta(t.cta_label || ""); setPreview(null); }, [t]);
  const dirty = subject !== t.subject || body !== t.body || cta !== (t.cta_label || "");

  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); } catch (e) { toast.error(e?.response?.data?.detail || "Something went wrong"); } finally { setBusy(false); }
  };
  const save = () => run(async () => { const { data } = await api.put(`/admin/email-templates/${t.key}`, { subject, body, cta_label: cta }); onSaved(data); }, "Template saved");
  const reset = async () => {
    if (!(await confirm({ title: "Reset to the default wording?", description: "Your edits to this email will be discarded.", confirmText: "Reset", destructive: true }))) return;
    run(async () => { const { data } = await api.delete(`/admin/email-templates/${t.key}`); onSaved(data); }, "Reset to default");
  };
  const doPreview = () => run(async () => { const { data } = await api.post(`/admin/email-templates/${t.key}/preview`, { subject, body, cta_label: cta }); setPreview(data); });
  const test = () => run(async () => { const { data } = await api.post(`/admin/email-templates/${t.key}/test`); toast.success(`Test email sent to ${data.to}`); });

  return (
    <div className="border border-slate-200 bg-white p-5 space-y-3" data-testid={`template-${t.key}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-display text-lg font-bold tracking-tight">{t.name}</div>
          <div className="text-xs text-slate-500">{t.description}</div>
        </div>
        {t.customised && <span className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-800 px-2 py-1 font-semibold" data-testid={`template-${t.key}-customised`}>Customised</span>}
      </div>
      <div className="flex flex-wrap gap-1">
        {t.placeholders.map((p) => (
          <button key={p} type="button" onClick={() => setBody((b) => b + `{${p}}`)} className="font-mono text-[11px] bg-slate-100 hover:bg-slate-200 px-1.5 py-0.5 rounded" title="Insert placeholder">{`{${p}}`}</button>
        ))}
      </div>
      <div>
        <label className="text-xs font-semibold text-slate-600">Subject</label>
        <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-1" data-testid={`template-${t.key}-subject`} />
      </div>
      <div>
        <label className="text-xs font-semibold text-slate-600">Body <span className="font-normal text-slate-400">— blank line = new paragraph, **bold**</span></label>
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={7} className="w-full border rounded-md p-2 text-sm mt-1 font-mono" data-testid={`template-${t.key}-body`} />
      </div>
      <div>
        <label className="text-xs font-semibold text-slate-600">Button label <span className="font-normal text-slate-400">(leave blank for no button)</span></label>
        <Input value={cta} onChange={(e) => setCta(e.target.value)} className="mt-1 max-w-xs" data-testid={`template-${t.key}-cta`} />
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={save} disabled={busy || !dirty} className="bg-[#0F1F2E] hover:bg-[#1a2f42]" data-testid={`template-${t.key}-save`}>Save</Button>
        <Button size="sm" variant="outline" onClick={doPreview} disabled={busy} data-testid={`template-${t.key}-preview`}><Eye className="w-4 h-4 mr-1" /> Preview</Button>
        <Button size="sm" variant="outline" onClick={test} disabled={busy || dirty} title={dirty ? "Save first" : "Send the saved version to your own email"} data-testid={`template-${t.key}-test`}><Send className="w-4 h-4 mr-1" /> Send test to me</Button>
        {t.customised && <Button size="sm" variant="ghost" onClick={reset} disabled={busy} className="text-rose-700" data-testid={`template-${t.key}-reset`}><RotateCcw className="w-4 h-4 mr-1" /> Reset to default</Button>}
      </div>
      {preview && (
        <div className="border border-slate-200 bg-slate-50 p-3" data-testid={`template-${t.key}-preview-pane`}>
          <div className="text-xs text-slate-500 mb-2">Subject: <span className="font-semibold text-slate-800">{preview.subject}</span></div>
          <iframe title="preview" srcDoc={preview.html} className="w-full h-[420px] bg-white border" sandbox="" />
        </div>
      )}
    </div>
  );
}

export default function AdminEmailTemplates() {
  const [templates, setTemplates] = useState([]);
  const [consent, setConsent] = useState(null);
  const load = async () => {
    try {
      const [{ data: t }, { data: c }] = await Promise.all([api.get("/admin/email-templates"), api.get("/admin/consent-status")]);
      setTemplates(t); setConsent(c);
    } catch { toast.error("Could not load templates"); }
  };
  useEffect(() => { load(); }, []);
  const onSaved = (data) => setTemplates((prev) => prev.map((x) => (x.key === data.key ? data : x)));

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-6" data-testid="email-templates-page">
      <div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">Email Templates</h1>
        <p className="text-sm text-slate-600 mt-1">Control exactly what students receive. Every email also carries the iTeams header, a privacy-notice link and a "Stop programme emails" link — those are fixed for GDPR.</p>
      </div>
      {consent && (
        <div className="border border-emerald-200 bg-emerald-50 p-4 text-sm flex flex-wrap gap-x-6 gap-y-1 items-center" data-testid="consent-status">
          <ShieldCheck className="w-5 h-5 text-emerald-700" />
          <span><strong>{consent.given}</strong> students consented</span>
          <span><strong>{consent.withdrawn}</strong> withdrawn</span>
          <span><strong>{consent.missing}</strong> not yet asked (prompted at next sign-in)</span>
          {consent.opted_out_emails.length > 0 && <span className="text-xs text-slate-600 w-full">Emails off: {consent.opted_out_emails.join(", ")}</span>}
        </div>
      )}
      {templates.length === 0 ? <div className="text-sm text-slate-500"><Mail className="inline w-4 h-4 mr-1" />Loading…</div>
        : templates.map((t) => <TemplateEditor key={t.key} t={t} onSaved={onSaved} />)}
    </div>
  );
}
